<template>
  <div class="auth-wrapper auth-v2">
    <div class="auth-inner">
      <v-avatar
        size="30px"
        color="#f4f5fa"
        class="brand-logo d-flex align-center me-3"
      >
        <v-img
          :src="appLogo"
          contain
          eager
        />
      </v-avatar>

      <v-row class="auth-row ma-0">
        <v-col
          lg="8"
          class="d-none d-lg-block position-relative overflow-hidden pa-0"
        >
          <div class="auth-illustrator-wrapper">
            <div class="d-flex align-center h-full pa-16 pe-0">
              <v-img
                contain
                max-width="100%"
                height="510"
                :src="require(`@/assets/images/logos/TECH_Horizontal_${$vuetify.theme.dark ? 'White' : 'Color'}.png`)"
              />
            </div>
          </div>
        </v-col>

        <v-col
          lg="4"
          class="d-flex align-center auth-bg pa-10 pb-0"
        >
          <v-row>
            <v-col
              cols="12"
              sm="8"
              md="6"
              lg="12"
              class="mx-auto"
            >
              <v-card flat>
                <v-card-text>
                  <p class="text-2xl font-weight-semibold text--primary mb-2">
                    Bem vindo!
                  </p>
                  <p class="mb-2">
                    Por favor, insira suas credenciais para realizar o login
                  </p>
                </v-card-text>

                <v-card-text>
                  <v-form
                    ref="loginForm"
                    @submit.prevent="handleFormSubmit"
                  >
                    <v-text-field
                      v-model="email"
                      class="mb-6"
                      outlined
                      label="E-mail"
                      placeholder="E-mail"
                      hide-details="auto"
                      :rules="[validators.required, validators.emailValidator]"
                    />

                    <v-text-field
                      v-model="password"
                      class="mb-2"
                      outlined
                      label="Senha"
                      placeholder="Senha"
                      hide-details="auto"
                      :type="isPasswordVisible ? 'text' : 'password'"
                      :append-icon="isPasswordVisible ? icons.mdiEyeOffOutline : icons.mdiEyeOutline"
                      :rules="[validators.required]"
                      @click:append="isPasswordVisible = !isPasswordVisible"
                    />

                    <div class="d-flex align-center justify-end flex-wrap">
                      <router-link
                        :to="{ name: 'auth-solicitar-troca-senha' }"
                        class="ms-3"
                      >
                        Esqueceu a senha?
                      </router-link>
                    </div>

                    <v-btn
                      block
                      color="primary"
                      type="submit"
                      class="mt-6"
                    >
                      <v-progress-circular
                        v-if="loading"
                        indeterminate
                        color="secundary"
                      />
                      <span v-else>Login</span>
                    </v-btn>
                  </v-form>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@core/preset/preset/pages/auth.scss';
</style>

<script>
import Vue from 'vue'
import jwtDecode from 'jwt-decode'
import { ref } from '@vue/composition-api'
import { mdiEyeOffOutline, mdiEyeOutline } from '@mdi/js'
import { emailValidator, required } from '@core/utils/validation'
import themeConfig from '@themeConfig'
import router from '@/router'
import AuthService from '@/services/modules/AuthService'
import useLocalStorage from '@/composables/useLocalStorage'
import { EventBus } from '@/utils/EventBus'
import useAuthStore from '@/store/auth'

export default {
  setup() {
    const email = ref('')
    const password = ref('')
    const isPasswordVisible = ref(false)

    const loginForm = ref(null)
    const loading = ref(false)

    const authStore = useAuthStore()

    async function login() {
      try {
        loading.value = true

        const response = await AuthService.login(email.value, password.value)

        if (!response.requerMFA) {
          /*
           * auth/login é o mesmo endpoint usado pelo autoatendimento: credencial de um Cliente
           * (sem cadastro de colaborador) é validada normalmente pelo Security e volta um token
           * com is_autoatendimento = true, quase sem claims de colaborador. Sem essa checagem, a
           * pessoa entrava no Flow mesmo sem ser colaborador -- a maioria das telas quebraria por
           * falta de claim, mas a sessão era aceita.
           */
          const { is_autoatendimento: isAutoatendimento } = jwtDecode(response.token)

          if (isAutoatendimento === 'true') {
            Vue.$toast.error('Este login é exclusivo para colaboradores.')
            return
          }

          await useLocalStorage(response.token)

          EventBus.$emit('logged-in')
          router.push('/')
        } else {
          authStore.setMfaCredentials(email.value, password.value)

          await AuthService.enviarEmailMfa({
            email: email.value,
            password: password.value,
          })

          Vue.$toast.success('Código enviado para seu e-mail.')

          router.push({ name: 'auth-mfa' })
        }
      } finally {
        loading.value = false
      }
    }

    async function handleFormSubmit() {
      if (loading.value) return
      if (loginForm.value.validate()) {
        await login()
      }
    }

    return {
      email,
      password,
      isPasswordVisible,
      loginForm,
      loading,
      handleFormSubmit,
      icons: {
        mdiEyeOutline,
        mdiEyeOffOutline,
      },
      validators: {
        required,
        emailValidator,
      },
      appName: themeConfig.app.name,
      appLogo: themeConfig.app.logo,
    }
  },
}
</script>
